import { useEffect, useMemo, useState } from 'react';
import {
  IconCash, IconCreditCard, IconBuildingBank, IconAlertCircle,
  IconLayoutColumns, IconUser, IconPlus, IconTrash, IconHeart, IconNotes,
  IconCheck,
} from '@tabler/icons-react';
import Modal from './ui/Modal';
import { sendOrEnqueue } from '../lib/outbox';
import { useManagerApproval } from '../hooks/useManagerApproval';
import { vatPortionOfInclusive } from '../lib/vat';
import { useSettings } from '../hooks/useSettings';

/**
 * PaymentModal — Phase 2.2.
 *
 * Owner rules wired here:
 *   - PRE-PAY ALWAYS: a payment is captured at the same time as the
 *     order. No "Pay later" — that comes back later as a Tab with its
 *     own settle screen (Phase 2.7).
 *   - VAT-INCLUSIVE: the displayed total IS the customer-paid number;
 *     the server re-computes VAT from this on save.
 *   - Card terminal is EXTERNAL (vendor-neutral — Yoco today, may
 *     switch). We don't talk to the device — the cashier taps the
 *     customer's card on the terminal, sees the approval, and
 *     confirms here. Phase 2.3 captures approval code, last-4, and
 *     txn reference for reconciliation.
 *
 * Method coverage in v1:
 *   - CASH (with tendered + change)
 *   - CARD_YOCO  (external card terminal — internal enum name only;
 *                  user-facing copy says "Card")
 *   - EFT        (manual bank transfer reference)
 *   - OTHER      (catch-all)
 * Account-charge (ACCOUNT) is wired in Phase 3 once Customer/Account models land.
 */

export interface PaymentLineInput {
  productId?: string | null;
  name: string;
  variantSize?: string | null;
  qty: number;
  unitPriceCents: number;
  excludedIngredients: string[];
  addons: { name: string; priceCents: number }[];
  note?: string | null;
  discountCents?: number;
  discountReason?: string | null;
  /** Phase 2.6b — supervisor-authorised override token (opaque). */
  priceOverrideToken?: string | null;
}

export type Channel = 'DINE_IN' | 'TAKEAWAY' | 'DELIVERY';

interface Props {
  open: boolean;
  onClose: () => void;
  totalCents: number;
  channel: Channel;
  lines: PaymentLineInput[];
  customer?: { id?: string; name?: string; phone?: string; email?: string };
  discountCents?: number;
  discountReason?: string;
  onSuccess: (order: { id: string; orderNumber: number; totalCents: number }) => void;
}

interface BranchAccount {
  id: string;
  name: string;
  status: 'ACTIVE' | 'SUSPENDED' | 'CLOSED';
  balanceCents: number;
  creditLimitCents: number;
  members: { id: string; name: string; isActive: boolean }[];
}

type Method = 'CASH' | 'CARD_YOCO' | 'EFT' | 'ACCOUNT';
type Flow = 'SINGLE' | 'SPLIT';

interface SplitLeg {
  id: string;
  method: Method;
  amountCents: number;
  // Method-specific bits captured at the moment the leg was added
  changeCents?: number;
  reference?: string | null;
  approvalCode?: string | null;
  last4?: string | null;
  txnId?: string | null;
}

interface MethodMeta {
  key: Method;
  label: string;
  Icon: typeof IconCash;
  hint: string;
}

const METHODS: MethodMeta[] = [
  { key: 'CASH',      label: 'Cash',     Icon: IconCash,         hint: 'Count + compute change' },
  { key: 'CARD_YOCO', label: 'Card',     Icon: IconCreditCard,   hint: 'External card terminal' },
  { key: 'EFT',       label: 'EFT',      Icon: IconBuildingBank, hint: 'Bank reference' },
];
const ACCOUNT_METHOD: MethodMeta = { key: 'ACCOUNT', label: 'Account', Icon: IconNotes, hint: 'Charge to house account' };

function fmtR(cents: number) {
  return `R ${(cents / 100).toFixed(2)}`;
}

export default function PaymentModal({ open, onClose, totalCents, channel, lines, customer, discountCents, discountReason, onSuccess }: Props) {
  const { settings } = useSettings();
  const splitEnabled = settings.splitBillEnabled;
  const tipsEnabled = settings.tipsEnabled;
  const askApproval = useManagerApproval();
  const [tipCents, setTipCents] = useState<number>(0);
  const [customTipInput, setCustomTipInput] = useState<string>('');
  const [flow, setFlow] = useState<Flow>('SINGLE');
  const [splits, setSplits] = useState<SplitLeg[]>([]);
  const [splitLegAmount, setSplitLegAmount] = useState<string>('');
  const [evenSplitN, setEvenSplitN] = useState<number>(2);
  const [method, setMethod] = useState<Method>('CASH');
  // Phase 7 — account picker state for ACCOUNT method.
  const [accounts, setAccounts] = useState<BranchAccount[]>([]);
  const [selectedAccountId, setSelectedAccountId] = useState<string>('');
  const [selectedMemberId, setSelectedMemberId] = useState<string>('');
  const [cashTendered, setCashTendered] = useState<string>('');
  const [eftReference, setEftReference] = useState<string>('');
  // Card fields — captured for reconciliation against the terminal's
  // end-of-day batch. Approval code is required (printed on the
  // terminal slip); last-4 + txn ref are optional but very helpful.
  const [cardApprovalCode, setCardApprovalCode] = useState<string>('');
  const [cardConfirmStep, setCardConfirmStep] = useState<'idle' | 'confirming' | 'confirmed'>('idle');
  const [cardLast4, setCardLast4] = useState<string>('');
  const [cardTxnRef, setCardTxnRef] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset state whenever the modal is freshly opened
  useEffect(() => {
    if (open) {
      setTipCents(0);
      setCustomTipInput('');
      setFlow('SINGLE');
      setSplits([]);
      setSplitLegAmount('');
      setEvenSplitN(2);
      setMethod('CASH');
      setCashTendered('');
      setEftReference('');
      setCardApprovalCode('');
      setCardConfirmStep('idle');
      setCardLast4('');
      setCardTxnRef('');
      setSelectedAccountId('');
      setSelectedMemberId('');
      setSubmitting(false);
      setError(null);
      // Fetch active accounts for the picker. Fail-safe — if the list
      // is empty, ACCOUNT method still shows but the picker will
      // explain "no accounts; create one in admin".
      (async () => {
        try {
          const r = await fetch('/api/accounts', { credentials: 'include' });
          if (!r.ok) return;
          const data = await r.json();
          const list = (data.accounts ?? [])
            .filter((a: { status: string }) => a.status === 'ACTIVE')
            .map((a: { id: string }) => a.id);
          // Fetch detailed (with members) for each — small list so this is cheap.
          const detailed = await Promise.all(
            list.map(async (id: string) => {
              const dr = await fetch(`/api/accounts/${id}`, { credentials: 'include' });
              if (!dr.ok) return null;
              const dd = await dr.json();
              return dd.account as BranchAccount;
            }),
          );
          setAccounts(detailed.filter((x): x is BranchAccount => !!x));
        } catch { /* swallow */ }
      })();
    }
  }, [open]);

  const tenderedCents = useMemo(() => {
    const v = Number(cashTendered.replace(',', '.'));
    if (!isFinite(v) || v < 0) return 0;
    return Math.round(v * 100);
  }, [cashTendered]);

  // Grand total includes any tip (tip is NOT VATable in SA — kept
  // outside the VAT-inclusive math).
  const grandTotalCents = totalCents + tipCents;

  // Single mode amounts
  const singleChangeCents = Math.max(0, tenderedCents - grandTotalCents);
  const vatCents = vatPortionOfInclusive(totalCents);
  const subtotalCents = totalCents - vatCents;

  // Split mode amounts
  const splitsTotalCents = splits.reduce((s, leg) => s + leg.amountCents, 0);
  const splitRemainingCents = Math.max(0, grandTotalCents - splitsTotalCents);
  const splitsOverpaid = splitsTotalCents > grandTotalCents;
  // Non-cash leg amount input (cash uses the existing `cashTendered` field)
  const legAmountFromInput = (() => {
    const v = Number(splitLegAmount.replace(',', '.'));
    if (!isFinite(v) || v <= 0) return 0;
    return Math.round(v * 100);
  })();
  // For CASH legs: take tendered, cap leg payment at remaining, surplus = change.
  // For non-cash legs: use the dedicated leg-amount input.
  const legAmountForPayment = method === 'CASH'
    ? Math.min(tenderedCents, splitRemainingCents)
    : Math.min(legAmountFromInput, splitRemainingCents);
  const legChangeCents = method === 'CASH'
    ? Math.max(0, tenderedCents - splitRemainingCents)
    : 0;

  const canSubmit = (() => {
    if (submitting) return false;
    if (grandTotalCents <= 0) return false;
    if (flow === 'SPLIT') {
      return splits.length > 0 && splitsTotalCents >= grandTotalCents && !splitsOverpaid;
    }
    if (method === 'CASH') return tenderedCents >= grandTotalCents;
    if (method === 'EFT') return eftReference.trim().length > 0;
    if (method === 'CARD_YOCO') return cardApprovalCode.trim().length > 0;
    if (method === 'ACCOUNT') return selectedAccountId.length > 0;
    return true;
  })();

  // Validity for "Add this split" leg in split mode
  const canAddLeg = (() => {
    if (legAmountForPayment <= 0) return false;
    if (method === 'EFT') return eftReference.trim().length > 0;
    if (method === 'CARD_YOCO') return cardApprovalCode.trim().length > 0;
    if (method === 'ACCOUNT') return selectedAccountId.length > 0;
    return true;
  })();

  function addSplitLeg() {
    if (!canAddLeg) return;
    const id = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;
    const leg: SplitLeg = (() => {
      if (method === 'CASH') {
        return { id, method: 'CASH', amountCents: legAmountForPayment, changeCents: legChangeCents };
      }
      if (method === 'CARD_YOCO') {
        return {
          id,
          method: 'CARD_YOCO',
          amountCents: legAmountForPayment,
          approvalCode: cardApprovalCode.trim() || null,
          last4: cardLast4.trim() || null,
          txnId: cardTxnRef.trim() || null,
        };
      }
      if (method === 'EFT') {
        return { id, method: 'EFT', amountCents: legAmountForPayment, reference: eftReference.trim() };
      }
      // ACCOUNT — fallthrough.
      return { id, method: 'ACCOUNT', amountCents: legAmountForPayment };
    })();
    setSplits((prev) => [...prev, leg]);
    // Reset per-leg fields and prepare next leg
    setSplitLegAmount('');
    setCashTendered('');
    setEftReference('');
    setCardApprovalCode('');
    setCardConfirmStep('idle');
    setCardLast4('');
    setCardTxnRef('');
  }

  function removeSplitLeg(id: string) {
    setSplits((prev) => prev.filter((l) => l.id !== id));
  }

  function applyEvenSplit() {
    if (evenSplitN < 2 || evenSplitN > 20) return;
    const base = Math.floor(grandTotalCents / evenSplitN);
    const remainder = grandTotalCents - base * evenSplitN;
    const newLegs: SplitLeg[] = Array.from({ length: evenSplitN }, (_, i) => ({
      id: (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now()}-${i}`,
      method: 'CASH',
      amountCents: base + (i === 0 ? remainder : 0),
    }));
    setSplits(newLegs);
  }

  function methodLabel(m: Method) {
    return m === 'CARD_YOCO' ? 'Card' : m === 'CASH' ? 'Cash' : m === 'EFT' ? 'EFT' : 'Other';
  }

  async function submit() {
    if (!canSubmit) return;
    setError(null);

    // Phase 3.2 — if any ACCOUNT-method leg would push the balance
    // over the credit limit, raise manager-PIN approval BEFORE we
    // submit. The server enforces the same gate (defence in depth).
    const selectedAccount = accounts.find((a) => a.id === selectedAccountId);
    if (selectedAccount) {
      const accountLegCharge =
        flow === 'SPLIT'
          ? splits.filter((l) => l.method === 'ACCOUNT').reduce((s, l) => s + l.amountCents, 0)
          : method === 'ACCOUNT'
            ? grandTotalCents
            : 0;
      if (accountLegCharge > 0) {
        const projected = selectedAccount.balanceCents + accountLegCharge;
        if (projected > selectedAccount.creditLimitCents) {
          const overBy = projected - selectedAccount.creditLimitCents;
          const ok = await askApproval({
            action: `Charge to account over limit`,
            description: `${selectedAccount.name} would exceed the credit limit by ${(overBy / 100).toFixed(2)}. Manager PIN required.`,
          });
          if (!ok) return;
        }
      }
    }

    setSubmitting(true);

    // Build the payments[] array. Single-mode = one leg; split-mode = N legs.
    const payments = (() => {
      if (flow === 'SPLIT') {
        return splits.map((leg) => {
          if (leg.method === 'CASH') {
            return { method: 'CASH' as const, amountCents: leg.amountCents, changeCents: leg.changeCents ?? 0 };
          }
          if (leg.method === 'CARD_YOCO') {
            return {
              method: 'CARD_YOCO' as const,
              amountCents: leg.amountCents,
              approvalCode: leg.approvalCode ?? undefined,
              last4: leg.last4 ?? null,
              txnId: leg.txnId ?? null,
            };
          }
          if (leg.method === 'EFT') {
            return { method: 'EFT' as const, amountCents: leg.amountCents, reference: leg.reference ?? '' };
          }
          // ACCOUNT
          return { method: 'ACCOUNT' as const, amountCents: leg.amountCents, accountId: selectedAccountId || undefined, accountMemberId: selectedMemberId || undefined };
        });
      }
      if (method === 'CASH') {
        return [{ method: 'CASH' as const, amountCents: grandTotalCents, changeCents: singleChangeCents }];
      }
      if (method === 'CARD_YOCO') {
        return [{
          method: 'CARD_YOCO' as const,
          amountCents: grandTotalCents,
          approvalCode: cardApprovalCode.trim(),
          last4: cardLast4.trim() || null,
          txnId: cardTxnRef.trim() || null,
        }];
      }
      if (method === 'EFT') {
        return [{ method: 'EFT' as const, amountCents: grandTotalCents, reference: eftReference.trim() }];
      }
      // ACCOUNT (final fallthrough — only 4 methods exist).
      return [{ method: 'ACCOUNT' as const, amountCents: grandTotalCents, accountId: selectedAccountId || undefined, accountMemberId: selectedMemberId || undefined }];
    })();

    const labelMethod = flow === 'SPLIT' ? `split×${payments.length}` : method;

    try {
      const r = await sendOrEnqueue<{ order: { id: string; orderNumber: number; totalCents: number } }>({
        url: '/api/orders',
        label: `Order R${(grandTotalCents / 100).toFixed(2)} — ${labelMethod}`,
        body: {
          channel,
          lines,
          payments,
          customerId: customer?.id ?? null,
          customerName: customer?.name ?? null,
          customerPhone: customer?.phone ?? null,
          customerEmail: customer?.email ?? null,
          discountCents: discountCents ?? 0,
          discountReason: discountReason ?? null,
          tipCents,
        },
      });

      if (r.status === 'queued') {
        // Offline — surface a friendly "saved, will sync" success.
        onSuccess({ id: r.idempotencyKey, orderNumber: 0, totalCents: grandTotalCents });
        return;
      }
      if (r.data?.order) {
        onSuccess(r.data.order);
        return;
      }
      // No data but no throw — unusual; treat as failure
      setError('Order placed but no confirmation received.');
    } catch (err) {
      const message = (err as Error).message ?? 'Failed to place order';
      setError(message.includes('409') ? 'Day session not open. Ask a supervisor to open the day.' : message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal open={open} onClose={submitting ? () => {} : onClose} size="lg" title="Take payment">
      {/* Summary strip */}
      <div className={tipsEnabled && tipCents > 0 ? "grid grid-cols-4 gap-3 mb-5" : "grid grid-cols-3 gap-3 mb-5"}>
        <div className="rounded-lg bg-light border border-border p-3">
          <p className="text-12 font-bold uppercase tracking-wider text-gray-700">Subtotal</p>
          <p className="text-18 font-bold text-heading tabular-nums mt-1">{fmtR(subtotalCents)}</p>
        </div>
        <div className="rounded-lg bg-light border border-border p-3">
          <p className="text-12 font-bold uppercase tracking-wider text-gray-700">VAT 15%</p>
          <p className="text-18 font-bold text-heading tabular-nums mt-1">{fmtR(vatCents)}</p>
        </div>
        {tipsEnabled && tipCents > 0 && (
          <div className="rounded-lg bg-success-soft border border-success/30 p-3">
            <p className="text-12 font-bold uppercase tracking-wider text-success">Tip</p>
            <p className="text-18 font-bold text-success tabular-nums mt-1">{fmtR(tipCents)}</p>
          </div>
        )}
        <div className="rounded-lg bg-primary-soft border border-primary/30 p-3">
          <p className="text-12 font-bold uppercase tracking-wider text-primary">
            {flow === 'SPLIT' ? 'Remaining' : 'Amount due'}
          </p>
          <p className="text-22 font-bold text-primary tabular-nums mt-1">
            {fmtR(flow === 'SPLIT' ? splitRemainingCents : grandTotalCents)}
          </p>
        </div>
      </div>

      {/* Tip picker — only shown when tips are enabled in settings */}
      {tipsEnabled && (
        <div className="mb-5 rounded-lg bg-light border border-border p-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-13 font-bold uppercase tracking-wider text-gray-700 inline-flex items-center gap-2">
              <IconHeart size={14} stroke={2.25} className="text-danger" />
              Add a tip
            </p>
            {tipCents > 0 && (
              <button
                type="button"
                onClick={() => { setTipCents(0); setCustomTipInput(''); }}
                className="text-12 font-bold text-danger hover:underline"
              >
                Clear tip
              </button>
            )}
          </div>
          <div className="grid grid-cols-5 gap-2 mb-3">
            {[0, 5, 10, 15, 20].map((pct) => {
              const computed = Math.round((totalCents * pct) / 100);
              const active = customTipInput === '' && tipCents === computed && pct > 0;
              const noneActive = customTipInput === '' && tipCents === 0 && pct === 0;
              return (
                <button
                  key={pct}
                  type="button"
                  onClick={() => { setTipCents(computed); setCustomTipInput(''); }}
                  className={[
                    'h-12 rounded-md border-2 text-13 font-bold transition-colors',
                    (active || noneActive)
                      ? 'bg-success text-[#FFFFFF] border-success'
                      : 'bg-surface border-border text-heading hover:border-success',
                  ].join(' ')}
                >
                  {pct === 0 ? 'No tip' : `${pct}%`}
                </button>
              );
            })}
          </div>
          <label className="block text-12 font-bold uppercase tracking-wider text-gray-700 mb-1.5">Custom amount (R)</label>
          <input
            type="text"
            inputMode="decimal"
            value={customTipInput}
            onChange={(e) => {
              const cleaned = e.target.value.replace(/[^0-9.,]/g, '');
              setCustomTipInput(cleaned);
              const v = Number(cleaned.replace(',', '.'));
              if (!isFinite(v) || v <= 0) {
                setTipCents(0);
              } else {
                setTipCents(Math.round(v * 100));
              }
            }}
            placeholder="e.g. 15.00"
            className="w-full h-11 px-4 rounded-md bg-surface border-2 border-border focus:border-success outline-none text-15 font-bold tabular-nums text-heading"
          />
        </div>
      )}

      {/* Flow toggle — only shown when split is enabled in settings */}
      {splitEnabled && (
        <div className="flex items-center justify-between mb-5 rounded-lg bg-light border border-border p-1.5">
          <button
            type="button"
            onClick={() => { setFlow('SINGLE'); setSplits([]); }}
            className={[
              'flex-1 h-10 inline-flex items-center justify-center gap-2 rounded-md text-13 font-bold transition-colors',
              flow === 'SINGLE' ? 'bg-primary text-[#FFFFFF] shadow-sm' : 'text-heading hover:bg-gray-100',
            ].join(' ')}
          >
            <IconUser size={16} stroke={2.25} />
            Single payment
          </button>
          <button
            type="button"
            onClick={() => setFlow('SPLIT')}
            className={[
              'flex-1 h-10 inline-flex items-center justify-center gap-2 rounded-md text-13 font-bold transition-colors',
              flow === 'SPLIT' ? 'bg-primary text-[#FFFFFF] shadow-sm' : 'text-heading hover:bg-gray-100',
            ].join(' ')}
          >
            <IconLayoutColumns size={16} stroke={2.25} />
            Split bill
          </button>
        </div>
      )}

      {/* Split-mode: legs list + even-split helper */}
      {flow === 'SPLIT' && (
        <div className="mb-5 rounded-lg bg-light border border-border p-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-13 font-bold uppercase tracking-wider text-gray-700">Splits added</p>
            <div className="flex items-center gap-2">
              <span className="text-12 font-bold text-gray-700">Even split:</span>
              <input
                type="number"
                min={2}
                max={20}
                value={evenSplitN}
                onChange={(e) => setEvenSplitN(Math.max(2, Math.min(20, Number(e.target.value) || 2)))}
                className="w-14 h-8 px-2 rounded-md bg-surface border border-border text-13 font-bold text-heading text-center"
              />
              <button
                type="button"
                onClick={applyEvenSplit}
                className="h-8 px-3 inline-flex items-center rounded-md bg-primary-soft text-primary text-12 font-bold uppercase tracking-wider hover:bg-primary hover:text-[#FFFFFF] transition-colors"
              >
                Apply
              </button>
            </div>
          </div>

          {splits.length === 0 ? (
            <p className="text-13 text-gray-700 italic">No splits yet — pick a method below and add one.</p>
          ) : (
            <ul className="space-y-2">
              {splits.map((leg, i) => (
                <li key={leg.id} className="flex items-center gap-3 p-2 rounded-md bg-surface border border-border">
                  <span className="w-7 h-7 rounded-md bg-primary text-[#FFFFFF] inline-flex items-center justify-center text-13 font-bold">{i + 1}</span>
                  <div className="flex-1">
                    <p className="text-14 font-bold text-heading">{methodLabel(leg.method)} · {fmtR(leg.amountCents)}</p>
                    {leg.changeCents && leg.changeCents > 0 && (
                      <p className="text-11 font-medium text-gray-700">Change {fmtR(leg.changeCents)}</p>
                    )}
                    {leg.approvalCode && (
                      <p className="text-11 font-medium text-gray-700">Approval {leg.approvalCode}{leg.last4 ? ` · **** ${leg.last4}` : ''}</p>
                    )}
                    {leg.reference && !leg.approvalCode && (
                      <p className="text-11 font-medium text-gray-700">Ref {leg.reference}</p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => removeSplitLeg(leg.id)}
                    className="w-8 h-8 inline-flex items-center justify-center rounded-md text-gray-500 hover:bg-danger-soft hover:text-danger"
                    title="Remove split"
                  >
                    <IconTrash size={16} stroke={2} />
                  </button>
                </li>
              ))}
            </ul>
          )}

          {splitRemainingCents > 0 && (
            <p className="mt-3 text-12 font-bold uppercase tracking-wider text-gray-700">
              Add a leg for the remaining {fmtR(splitRemainingCents)}.
            </p>
          )}
          {splitsOverpaid && (
            <p className="mt-3 text-12 font-bold uppercase tracking-wider text-danger">
              Splits exceed the total by {fmtR(splitsTotalCents - grandTotalCents)}. Remove or adjust a leg.
            </p>
          )}
        </div>
      )}

      {/* Method picker — hidden in split mode once all paid */}
      {(flow === 'SINGLE' || splitRemainingCents > 0) && (
        <>
          <p className="text-13 font-bold uppercase tracking-wider text-gray-700 mb-2">
            {flow === 'SPLIT' ? 'Method for this leg' : 'Payment method'}
          </p>
          <div className="grid grid-cols-4 gap-2 mb-5">
            {[
              ...METHODS,
              ACCOUNT_METHOD,
            ].map((m) => {
              const active = method === m.key;
              return (
                <button
                  key={m.key}
                  type="button"
                  onClick={() => { setMethod(m.key); setCardConfirmStep('idle'); setCardApprovalCode(''); }}
                  className={[
                    'h-20 rounded-lg border-2 flex flex-col items-center justify-center gap-1 transition-all',
                    active
                      ? 'bg-primary text-[#FFFFFF] border-primary shadow-md'
                      : 'bg-surface border-border text-heading hover:border-primary',
                  ].join(' ')}
                >
                  <m.Icon size={22} stroke={2} />
                  <span className="text-13 font-bold">{m.label}</span>
                </button>
              );
            })}
          </div>
        </>
      )}

      {/* Non-cash leg-amount input (split mode only) — cash uses the
          tender field below as both leg amount and change source. */}
      {flow === 'SPLIT' && method !== 'CASH' && splitRemainingCents > 0 && (
        <div className="mb-3 rounded-lg bg-light border border-border p-4">
          <label className="block text-13 font-bold uppercase tracking-wider text-gray-700 mb-2">
            This leg amount (R)
          </label>
          <input
            type="text"
            inputMode="decimal"
            value={splitLegAmount}
            onChange={(e) => setSplitLegAmount(e.target.value.replace(/[^0-9.,]/g, ''))}
            placeholder={(splitRemainingCents / 100).toFixed(2)}
            className="w-full h-12 px-4 rounded-md bg-surface border-2 border-border focus:border-primary outline-none text-18 font-bold tabular-nums text-heading"
          />
          <button
            type="button"
            onClick={() => setSplitLegAmount((splitRemainingCents / 100).toFixed(2))}
            className="mt-2 text-12 font-bold text-primary hover:underline"
          >
            Use remaining {fmtR(splitRemainingCents)}
          </button>
        </div>
      )}

      {/* Method-specific fields */}
      {method === 'CASH' && (splitRemainingCents > 0 || flow === 'SINGLE') && (
        <div className="mb-5 rounded-lg bg-light border border-border p-4">
          <label className="block text-13 font-bold uppercase tracking-wider text-gray-700 mb-2">
            {flow === 'SPLIT' ? 'Cash for this leg (R)' : 'Cash tendered (R)'}
          </label>
          <input
            type="text"
            inputMode="decimal"
            value={cashTendered}
            onChange={(e) => setCashTendered(e.target.value.replace(/[^0-9.,]/g, ''))}
            placeholder={((flow === 'SPLIT' ? splitRemainingCents : grandTotalCents) / 100).toFixed(2)}
            className="w-full h-12 px-4 rounded-md bg-surface border-2 border-border focus:border-primary outline-none text-18 font-bold tabular-nums text-heading"
          />
          {/* Quick chips — sized to remaining (split) or total (single) */}
          <div className="grid grid-cols-4 gap-2 mt-3">
            {(() => {
              const target = flow === 'SPLIT' ? splitRemainingCents : grandTotalCents;
              return [
                Math.ceil(target / 100),
                Math.ceil(target / 100 / 10) * 10,
                Math.ceil(target / 100 / 50) * 50,
                Math.ceil(target / 100 / 100) * 100,
              ]
                .filter((v, i, arr) => arr.indexOf(v) === i)
                .map((rand) => (
                  <button
                    key={rand}
                    type="button"
                    onClick={() => setCashTendered(String(rand))}
                    className="h-10 rounded-md bg-surface border border-border text-14 font-bold text-heading hover:border-primary hover:text-primary transition-colors"
                  >
                    R {rand}
                  </button>
                ));
            })()}
          </div>
          {flow === 'SINGLE' && tenderedCents >= grandTotalCents && (
            <div className="mt-3 flex items-center justify-between px-3 h-12 rounded-md bg-success-soft border border-success/30">
              <span className="text-14 font-bold text-success uppercase tracking-wider">Change due</span>
              <span className="text-22 font-bold text-success tabular-nums">{fmtR(singleChangeCents)}</span>
            </div>
          )}
          {flow === 'SPLIT' && tenderedCents > splitRemainingCents && (
            <div className="mt-3 flex items-center justify-between px-3 h-12 rounded-md bg-success-soft border border-success/30">
              <span className="text-14 font-bold text-success uppercase tracking-wider">Change due</span>
              <span className="text-22 font-bold text-success tabular-nums">{fmtR(legChangeCents)}</span>
            </div>
          )}
        </div>
      )}

      {method === 'CARD_YOCO' && (splitRemainingCents > 0 || flow === 'SINGLE') && (
        <div className="mb-5 rounded-lg bg-primary-soft border-2 border-primary/30 p-5 space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-primary text-[#FFFFFF] inline-flex items-center justify-center flex-shrink-0">
              <IconCreditCard size={26} stroke={2.25} />
            </div>
            <div>
              <p className="text-16 font-bold text-heading">Use the card terminal</p>
              <p className="text-13 text-body mt-0.5">Charge <strong className="text-heading tabular-nums">{fmtR(flow === 'SPLIT' ? legAmountForPayment : grandTotalCents)}</strong> on the machine, then confirm below.</p>
            </div>
          </div>

          {cardConfirmStep === 'idle' ? (
            <button
              type="button"
              onClick={() => setCardConfirmStep('confirming')}
              className="w-full h-14 rounded-md bg-primary text-[#FFFFFF] text-15 font-bold hover:bg-primary-hover transition-colors shadow-sm inline-flex items-center justify-center gap-2"
            >
              <IconCreditCard size={18} stroke={2.5} />
              Transaction approved
            </button>
          ) : (
            <div className="rounded-md bg-danger-soft border-2 border-danger p-4 space-y-3">
              <p className="text-15 font-bold text-danger leading-snug">
                Are you sure the transaction was approved on the card machine?
              </p>
              <p className="text-13 text-danger/80">
                Only confirm after the terminal displays "APPROVED". This will mark the sale complete.
              </p>
              <div className="flex items-stretch gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => setCardConfirmStep('idle')}
                  className="flex-1 h-12 rounded-md bg-light border-2 border-border text-14 font-bold text-heading hover:bg-gray-100"
                >
                  No — retry
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setCardApprovalCode('APPROVED');
                    setCardConfirmStep('confirmed');
                  }}
                  className="flex-[2] h-12 rounded-md bg-danger text-[#FFFFFF] text-14 font-bold hover:bg-danger-hover shadow-sm inline-flex items-center justify-center gap-2"
                >
                  <IconCheck size={16} stroke={2.5} />
                  Yes — approved on terminal
                </button>
              </div>
            </div>
          )}

          {cardConfirmStep === 'confirmed' && (
            <div className="rounded-md bg-success-soft border border-success/30 p-3 flex items-center gap-2">
              <IconCheck size={18} className="text-success" stroke={2.5} />
              <p className="text-13 font-bold text-success">
                {flow === 'SPLIT'
                  ? 'Add this leg below, or tap Charge to complete.'
                  : 'Tap Charge to complete the sale.'}
              </p>
            </div>
          )}
        </div>
      )}

      {method === 'EFT' && (splitRemainingCents > 0 || flow === 'SINGLE') && (
        <div className="mb-5 rounded-lg bg-light border border-border p-4">
          <label className="block text-13 font-bold uppercase tracking-wider text-gray-700 mb-2">Bank reference</label>
          <input
            type="text"
            autoFocus
            value={eftReference}
            onChange={(e) => setEftReference(e.target.value)}
            placeholder="e.g. last 4 of customer's name + date"
            className="w-full h-12 px-4 rounded-md bg-surface border-2 border-border focus:border-primary outline-none text-15 font-semibold text-heading"
          />
        </div>
      )}

      {method === 'ACCOUNT' && (splitRemainingCents > 0 || flow === 'SINGLE') && (() => {
        if (accounts.length === 0) {
          return (
            <div className="mb-5 rounded-lg bg-warning-soft border border-warning/30 p-4 flex items-start gap-3">
              <IconNotes size={20} className="text-warning flex-shrink-0 mt-0.5" />
              <p className="text-13 font-semibold text-warning">
                No accounts in this branch yet. An admin needs to create one in <strong>/admin/accounts</strong> first.
              </p>
            </div>
          );
        }
        const sel = accounts.find((a) => a.id === selectedAccountId);
        const activeMembers = sel?.members.filter((m) => m.isActive) ?? [];
        const charge = flow === 'SPLIT' ? legAmountForPayment : grandTotalCents;
        const projected = sel ? sel.balanceCents + charge : 0;
        const remaining = sel ? Math.max(0, sel.creditLimitCents - sel.balanceCents) : 0;
        const overLimit = sel ? projected > sel.creditLimitCents : false;
        return (
          <div className="mb-5 rounded-lg bg-light border border-border p-4 space-y-3">
            <div>
              <label className="block text-12 font-bold uppercase tracking-wider text-gray-700 mb-1.5">Account</label>
              <select
                value={selectedAccountId}
                onChange={(e) => { setSelectedAccountId(e.target.value); setSelectedMemberId(''); }}
                className="w-full h-11 px-3 rounded-md bg-surface border-2 border-border focus:border-primary outline-none text-14 font-semibold text-heading"
              >
                <option value="">— select an account —</option>
                {accounts.map((a) => (
                  <option key={a.id} value={a.id}>{a.name} · bal {fmtR(a.balanceCents)}</option>
                ))}
              </select>
            </div>
            {sel && (
              <>
                {activeMembers.length > 0 && (
                  <div>
                    <label className="block text-12 font-bold uppercase tracking-wider text-gray-700 mb-1.5">Who placed it? (optional)</label>
                    <select
                      value={selectedMemberId}
                      onChange={(e) => setSelectedMemberId(e.target.value)}
                      className="w-full h-11 px-3 rounded-md bg-surface border-2 border-border focus:border-primary outline-none text-14 font-semibold text-heading"
                    >
                      <option value="">— no member —</option>
                      {activeMembers.map((m) => (
                        <option key={m.id} value={m.id}>{m.name}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="grid grid-cols-3 gap-2 text-13">
                  <div className="rounded-md bg-surface border border-border p-2">
                    <p className="text-11 font-bold uppercase tracking-wider text-gray-700">Balance now</p>
                    <p className="font-bold text-heading tabular-nums">{fmtR(sel.balanceCents)}</p>
                  </div>
                  <div className="rounded-md bg-surface border border-border p-2">
                    <p className="text-11 font-bold uppercase tracking-wider text-gray-700">After this</p>
                    <p className={`font-bold tabular-nums ${overLimit ? 'text-danger' : 'text-heading'}`}>{fmtR(projected)}</p>
                  </div>
                  <div className="rounded-md bg-surface border border-border p-2">
                    <p className="text-11 font-bold uppercase tracking-wider text-gray-700">Limit</p>
                    <p className="font-bold text-heading tabular-nums">{fmtR(sel.creditLimitCents)}</p>
                  </div>
                </div>
                {overLimit ? (
                  <p className="text-12 font-semibold text-danger">
                    Over credit limit by {fmtR(projected - sel.creditLimitCents)}. Manager PIN required.
                  </p>
                ) : (
                  <p className="text-12 text-gray-700">Credit remaining: {fmtR(remaining)}</p>
                )}
              </>
            )}
          </div>
        );
      })()}

      {/* Split-mode: Add this leg */}
      {flow === 'SPLIT' && splitRemainingCents > 0 && (
        <button
          type="button"
          onClick={addSplitLeg}
          disabled={!canAddLeg}
          className="w-full h-12 mb-5 inline-flex items-center justify-center gap-2 rounded-md bg-primary-soft border-2 border-primary text-primary text-14 font-bold hover:bg-primary hover:text-[#FFFFFF] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <IconPlus size={18} stroke={2.5} />
          Add this split {legAmountForPayment > 0 ? fmtR(legAmountForPayment) : ''}
        </button>
      )}

      {error && (
        <div className="mb-4 rounded-md bg-danger-soft border border-danger/30 p-3 flex items-start gap-2">
          <IconAlertCircle size={18} className="text-danger flex-shrink-0 mt-0.5" />
          <p className="text-13 font-semibold text-danger">{error}</p>
        </div>
      )}

      <div className="flex items-stretch gap-2">
        <button
          type="button"
          onClick={onClose}
          disabled={submitting}
          className="flex-1 h-12 rounded-md bg-light border border-border text-14 font-bold text-heading hover:bg-gray-100 disabled:opacity-50 transition-colors"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={!canSubmit}
          className="flex-[2] h-12 rounded-md bg-success text-[#FFFFFF] text-15 font-bold hover:bg-success-hover disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors shadow-md"
        >
          {submitting
            ? 'Placing order…'
            : flow === 'SPLIT'
              ? `Confirm ${splits.length} ${splits.length === 1 ? 'split' : 'splits'} · ${fmtR(splitsTotalCents)}`
              : `Charge ${fmtR(grandTotalCents)}`}
        </button>
      </div>
    </Modal>
  );
}
